import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { collection, onSnapshot, orderBy, query, where, getDocs } from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { db } from "../../data/firebase";
import { tokens } from "../../theme";
import Header from "../../components/Global/Header";
import { AuthContext } from "../../context/AuthContext";
import usePermissions from "../../hooks/usePermissions";
import { groupIntoBlocks, overlapsAny, hasInternalOverlap } from "../../utils/coverBlocks";
import { useLocalPrefs } from "../../utils/localPrefs";
import {
  canManageCovers,
  fetchSeniorTutorAssignment,
  fmtDay,
  fmtRange,
  getWeekStart,
  subscribeCoverTemplates,
} from "../../utils/coversApi";
import CreateCoverGroupDialog from "../../components/Covers/CreateCoverGroupDialog";
import CoverGroupDetailDialog from "../../components/Covers/CoverGroupDetailDialog";
import CoverTemplatesDialog from "../../components/Covers/CoverTemplatesDialog";
import CoverRequestStrip, { timeAgo } from "../../components/Covers/CoverRequestStrip";

const tsMs = (ts) => (ts?.toDate ? ts.toDate().getTime() : ts ? new Date(ts).getTime() : 0);

const CoversPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { currentUser } = useContext(AuthContext);
  const { role, loading: permsLoading } = usePermissions();
  const [prefs, setPrefs] = useLocalPrefs();
  const byTutor = prefs.coversGroupBy !== "time";
  const grouping = useMemo(() => ({ byTutor }), [byTutor]);

  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [lessons, setLessons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [detailGroupId, setDetailGroupId] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [hasSavedTemplates, setHasSavedTemplates] = useState(false);
  const [, setTick] = useState(0);
  const [filter, setFilter] = useState("all"); // all | pending | notsent | covered

  const weekEnd = useMemo(() => weekStart.add(7, "day"), [weekStart]);
  const isManager = canManageCovers(role, currentUser?.uid, assignment?.tutorId);

  // Re-render every minute so "sent 5 minutes ago" stays current.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Senior tutor on duty for the week being viewed.
  useEffect(() => {
    fetchSeniorTutorAssignment(weekStart.toDate()).then(setAssignment).catch(() => setAssignment(null));
  }, [weekStart]);

  // Message templates (shared setting).
  useEffect(() => {
    const unsub = subscribeCoverTemplates((list, saved) => {
      setTemplates(list);
      setHasSavedTemplates(saved);
    });
    return () => unsub();
  }, []);

  // All tutors (for suggestions and names).
  useEffect(() => {
    getDocs(collection(db, "tutors")).then((snap) =>
      setTutors(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  // Lessons for the week (filtered to Unconfirmed client-side to avoid a composite index).
  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, "lessons"),
      where("startDateTime", ">=", weekStart.toISOString()),
      where("startDateTime", "<", weekEnd.toISOString())
    );
    const unsub = onSnapshot(q, (snap) => {
      setLessons(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, [weekStart, weekEnd]);

  // Cover groups whose first session falls in the week.
  useEffect(() => {
    const q = query(
      collection(db, "coverGroups"),
      where("firstStart", ">=", weekStart.toISOString()),
      where("firstStart", "<", weekEnd.toISOString()),
      orderBy("firstStart", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setGroups(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [weekStart, weekEnd]);

  // Managers see every unconfirmed session; tutors only their own.
  const mine = useCallback(
    (l) => isManager || l.tutorId === currentUser?.uid,
    [isManager, currentUser?.uid]
  );
  const unconfirmed = useMemo(
    () => lessons.filter((l) => l.type === "Unconfirmed" && mine(l)),
    [lessons, mine]
  );

  // "New": marked Unconfirmed within the user's chosen window (per-browser setting).
  const newCutoff = prefs.coversShowNew ? Date.now() - prefs.coversNewHours * 3600000 : Infinity;
  const isNew = useCallback((l) => tsMs(l.unconfirmedAt) >= newCutoff, [newCutoff]);

  const openGroups = useMemo(() => groups.filter((g) => g.status === "open"), [groups]);

  // Sessions that sit in an open request always form their own block (one per
  // request), so the remaining sessions of the original block stand apart and
  // can still be selected.
  const { newBlocks, blocks } = useMemo(() => {
    const requestedIds = new Set(openGroups.flatMap((g) => g.lessonIds || []));
    const requestedBlocks = openGroups
      .map((g) => {
        const ls = unconfirmed.filter((l) => (g.lessonIds || []).includes(l.id));
        if (!ls.length) return null;
        const [b] = groupIntoBlocks(ls, 1e9, { byTutor: false });
        b.id = `req_${g.id}`;
        b.requestGroup = g;
        return b;
      })
      .filter(Boolean);
    const free = unconfirmed.filter((l) => !requestedIds.has(l.id));
    const byStart = (a, b) => a.startMs - b.startMs;
    return {
      newBlocks: [
        ...groupIntoBlocks(free.filter(isNew), 0, grouping),
        ...requestedBlocks.filter((b) => b.lessons.some(isNew)),
      ].sort(byStart),
      blocks: [
        ...groupIntoBlocks(free.filter((l) => !isNew(l)), 0, grouping),
        ...requestedBlocks.filter((b) => !b.lessons.some(isNew)),
      ].sort(byStart),
    };
  }, [unconfirmed, openGroups, isNew, grouping]);

  // Sessions already covered this week, grouped under the tutor they were covered for.
  const coveredBlocks = useMemo(() => {
    // A lesson counts as covered only while it is still Normal. If it is marked
    // Unconfirmed again (the cover tutor is now unavailable) it goes back to the list.
    const covered = lessons
      .filter(
        (l) =>
          l.coverGroupId &&
          l.type === "Normal" &&
          (isManager || l.coveredForTutorId === currentUser?.uid || l.tutorId === currentUser?.uid)
      )
      .map((l) => ({
        ...l,
        coverTutorId: l.tutorId,
        coverTutorName: l.tutorName,
        tutorId: l.coveredForTutorId || "",
        tutorName: l.coveredForTutorName || "Unassigned",
      }));
    return groupIntoBlocks(covered, 0, grouping).map((b) => ({ ...b, covered: true }));
  }, [lessons, grouping, isManager, currentUser?.uid]);

  // Groups a viewer may see: managers see all; tutors see ones they sent or were invited to.
  const visibleGroups = useMemo(
    () =>
      isManager
        ? groups
        : groups.filter(
            (g) =>
              g.createdBy === currentUser?.uid ||
              (g.invitedTutorIds || []).includes(currentUser?.uid) ||
              (g.originalTutorIds || [g.originalTutorId]).includes(currentUser?.uid)
          ),
    [groups, isManager, currentUser?.uid]
  );

  // lessonId -> groups touching it (newest first).
  const groupsByLesson = useMemo(() => {
    const map = new Map();
    [...visibleGroups]
      .sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt))
      .forEach((g) =>
        (g.lessonIds || []).forEach((id) => {
          if (!map.has(id)) map.set(id, []);
          map.get(id).push(g);
        })
      );
    return map;
  }, [visibleGroups]);

  const openGroupByLesson = useMemo(() => {
    const map = new Map();
    groups
      .filter((g) => g.status === "open")
      .forEach((g) => (g.lessonIds || []).forEach((id) => map.set(id, g)));
    return map;
  }, [groups]);

  const groupById = useMemo(() => new Map(visibleGroups.map((g) => [g.id, g])), [visibleGroups]);

  // Requests to show inside a block.
  // - Covered block: the request that covered it (by the lesson's own coverGroupId).
  // - Unconfirmed block: every open request, else the most recent finished one, but
  //   only from the current round: finished requests created before the lesson was
  //   last marked Unconfirmed belong to an earlier episode and are ignored.
  const requestsForBlock = useCallback(
    (block) => {
      if (block.covered) {
        const ids = [...new Set(block.lessons.map((l) => l.coverGroupId).filter(Boolean))];
        return ids.map((id) => groupById.get(id)).filter(Boolean);
      }
      const seen = new Map();
      block.lessons.forEach((l) => (groupsByLesson.get(l.id) || []).forEach((g) => seen.set(g.id, g)));
      const all = [...seen.values()].sort((a, b) => tsMs(b.createdAt) - tsMs(a.createdAt));
      const open = all.filter((g) => g.status === "open");
      if (open.length) return open;
      // A "taken" request means these sessions were reassigned; if they are
      // unconfirmed again that cover is over, so never show it here.
      const finished = all.filter((g) => g.status !== "taken");
      const roundStart = Math.min(...block.lessons.map((l) => tsMs(l.unconfirmedAt) || 0));
      const thisRound = finished.filter((g) => tsMs(g.createdAt) >= roundStart - 60000);
      return thisRound.slice(0, 1);
    },
    [groupsByLesson, groupById]
  );

  const selectedLessons = useMemo(
    () => unconfirmed.filter((l) => selected.has(l.id)),
    [unconfirmed, selected]
  );
  const selectedTutorCount = new Set(selectedLessons.map((l) => l.tutorId || "")).size;

  const toggle = useCallback((ids, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const changeWeek = (delta) => {
    setSelected(new Set());
    setWeekStart((w) => (delta === 0 ? getWeekStart(new Date()) : w.add(delta, "week")));
  };

  const weekLabel = `${weekStart.format("ddd D MMM")} – ${weekStart.add(6, "day").format("ddd D MMM YYYY")}`;
  const openCount = groups.filter((g) => g.status === "open").length;
  const coveredCount = coveredBlocks.reduce((n, b) => n + b.lessons.length, 0);
  const notSentCount = unconfirmed.filter((l) => !openGroupByLesson.has(l.id)).length;
  const notSentOnly = (list) =>
    filter === "notsent" ? list.filter((b) => b.lessons.some((l) => !openGroupByLesson.has(l.id))) : list;
  const shownBlocks = notSentOnly(blocks);
  const shownNewBlocks = filter === "covered" ? [] : notSentOnly(newBlocks);
  const headingText =
    filter === "covered" ? "Covered sessions" : filter === "notsent" ? "Not yet sent" : "Unconfirmed sessions";
  const headingCount = filter === "covered" ? coveredCount : filter === "notsent" ? notSentCount : unconfirmed.length;

  const renderStrips = (block) =>
    requestsForBlock(block).map((g) => (
      <CoverRequestStrip
        key={g.id}
        group={g}
        expanded={expanded.has(g.id)}
        onToggle={() => toggleExpanded(g.id)}
        onManage={() => setDetailGroupId(g.id)}
        isManager={isManager || g.createdBy === currentUser?.uid}
        isDark={isDark}
      />
    ));

  const renderBlock = (block, isNewBlock) => {
    const selectable = block.lessons.filter((l) => !openGroupByLesson.has(l.id));
    const allSelected = selectable.length > 0 && selectable.every((l) => selected.has(l.id));
    const someSelected = selectable.some((l) => selected.has(l.id));
    // A cover tutor can't be in two places at once: never allow concurrent sessions together.
    const blockHasConcurrent = hasInternalOverlap(selectable);
    const otherSelected = selectedLessons.filter((l) => !block.lessons.some((b) => b.id === l.id));
    const blockClashesOutside = selectable.some((l) => overlapsAny(l, otherSelected));
    const blockCheckDisabled = selectable.length === 0 || blockHasConcurrent || (!allSelected && blockClashesOutside);
    const blockCheckTitle = blockHasConcurrent
      ? "This block has sessions at the same time. Tick them individually."
      : blockClashesOutside
        ? "Overlaps a session you've already selected"
        : "";
    const hasOpen = block.lessons.some((l) => openGroupByLesson.has(l.id));
    const allDeclined = requestsForBlock(block).some((g) => {
      const ids = g.invitedTutorIds || [];
      return g.status === "open" && ids.length > 0 && ids.every((id) => g.invited?.[id]?.response === "declined");
    });
    const border = allDeclined
      ? `1px solid ${isDark ? "#C62828" : "#EF9A9A"}`
      : hasOpen
      ? `1px solid ${isDark ? "#B98900" : "#F9C74F"}`
      : isNewBlock
        ? `1px solid ${isDark ? "#1E88E5" : "#90CAF9"}`
        : "1px solid transparent";
    return (
      <Paper
        key={block.id}
        data-testid="cover-block"
        data-new={isNewBlock ? "true" : "false"}
        sx={{ overflow: "hidden", border }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: allDeclined
              ? isDark ? "rgba(239,83,80,0.10)" : "#FDECEA"
              : hasOpen ? (isDark ? "rgba(249,199,79,0.10)" : "#FFF8E1") : colors.primary[400],
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderLeft: `6px solid ${allDeclined ? "#D32F2F" : hasOpen ? "#F9A825" : block.lessons[0]?.tutorColor || colors.orangeAccent[400]}`,
          }}
        >
          {!hasOpen && (
            <Tooltip title={blockCheckTitle}>
              <span>
                <Checkbox
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  disabled={blockCheckDisabled}
                  onChange={(e) => toggle(selectable.map((l) => l.id), e.target.checked)}
                  inputProps={{ "aria-label": `Select block ${block.tutorName}` }}
                />
              </span>
            </Tooltip>
          )}
          <Box flex={1}>
            <Typography variant="h5" fontWeight="bold">
              {block.mixed ? `${block.tutorIds.length} tutors` : block.tutorName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {fmtDay(block.lessons[0].startDateTime)} · {fmtRange(block.startMs, block.endMs)} ·{" "}
              {block.hours} hr{block.hours === 1 ? "" : "s"}
              {block.locationNames.length > 0 && ` · ${block.locationNames.join(", ")}`}
              {block.mixed && ` · ${block.tutorNames.join(", ")}`}
            </Typography>
          </Box>
          {isNewBlock && (
            <Chip size="small" color="info" label={`New · ${timeAgo(block.lessons[0]?.unconfirmedAt)}`} />
          )}
          {hasOpen && (
            <Chip
              size="small"
              color={allDeclined ? "error" : "warning"}
              label={allDeclined ? "All declined" : "Request sent"}
            />
          )}
          <Chip size="small" label={`${block.lessons.length} session${block.lessons.length === 1 ? "" : "s"}`} />
        </Box>

        {renderStrips(block)}

        <Box>
          {block.lessons.map((l) => {
            const isSelected = selected.has(l.id);
            const clashes = !isSelected && overlapsAny(l, selectedLessons);
            return (
              <Box
                key={l.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${colors.primary[500]}`,
                  bgcolor: selected.has(l.id) ? `${colors.orangeAccent[400]}18` : "transparent",
                }}
              >
                {!hasOpen && (
                  <Tooltip title={clashes ? "Same time as a session you've already selected" : ""}>
                    <span>
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        disabled={clashes}
                        onChange={(e) => toggle([l.id], e.target.checked)}
                        inputProps={{ "aria-label": `Select session ${l.subjectGroupName}` }}
                      />
                    </span>
                  </Tooltip>
                )}
                <Typography sx={{ minWidth: 150 }} fontWeight="bold">
                  {fmtRange(l.startDateTime, l.endDateTime)}
                </Typography>
                <Box flex={1}>
                  <Typography>{l.subjectGroupName || "Lesson"}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(l.studentNames || []).join(", ") || "No students"}
                    {l.locationName && ` · ${l.locationName}`}
                  </Typography>
                </Box>
                {block.mixed && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={l.tutorName || "Unassigned"}
                    sx={{ borderColor: l.tutorColor || undefined }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  return (
    <Box m="20px">
      <ToastContainer position="top-right" autoClose={4000} />
      <Header title="COVERS" subtitle="Find cover for unconfirmed sessions" />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton onClick={() => changeWeek(-1)} aria-label="Previous week">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h4" fontWeight="bold" sx={{ minWidth: 300, textAlign: "center" }}>
              {weekLabel}
            </Typography>
            <IconButton onClick={() => changeWeek(1)} aria-label="Next week">
              <ChevronRightIcon />
            </IconButton>
            <Button size="small" startIcon={<TodayIcon />} onClick={() => changeWeek(0)}>
              This week
            </Button>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              Senior tutor on duty: <b>{assignment?.tutorName || "not assigned"}</b>
            </Typography>
            {isManager && (
              <Tooltip title="Cover message templates">
                <IconButton aria-label="Cover message templates" onClick={() => setTemplatesOpen(true)}>
                  <SettingsOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>

      {!permsLoading && !isManager && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These are your own unconfirmed sessions. Tick the ones you need covered and send a request;
          it goes to the tutors you choose as a message from you. The senior tutor on duty can see
          every request and will step in if needed.
        </Alert>
      )}

      <Box maxWidth={1100}>
        <Stack direction="row" alignItems="center" gap={2} mb={1} flexWrap="wrap">
          <Typography variant="h4" fontWeight="bold">
            {headingText}{" "}
            <Typography component="span" variant="h5" color="text.secondary">
              ({headingCount})
            </Typography>
          </Typography>
          {openCount > 0 && filter !== "covered" && (
            <Chip size="small" color="warning" label={`${openCount} open request${openCount === 1 ? "" : "s"}`} />
          )}
          <Box flex={1} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={byTutor ? "tutor" : "time"}
            onChange={(_, v) => v && setPrefs({ coversGroupBy: v })}
            aria-label="Group sessions"
          >
            <ToggleButton value="tutor" title="One block per rostered tutor">
              By tutor
            </ToggleButton>
            <ToggleButton value="time" title="Join back-to-back sessions even if they belong to different tutors">
              By time
            </ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filter}
            onChange={(_, v) => v && setFilter(v)}
            aria-label="Filter sessions"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pending">Pending{unconfirmed.length > 0 && ` (${unconfirmed.length})`}</ToggleButton>
            <ToggleButton value="notsent">Not sent{notSentCount > 0 && ` (${notSentCount})`}</ToggleButton>
            <ToggleButton value="covered">Covered{coveredCount > 0 && ` (${coveredCount})`}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {filter === "covered" ? null : loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : shownBlocks.length === 0 && shownNewBlocks.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              {filter === "notsent"
                ? "Every unconfirmed session already has a cover request."
                : "No unconfirmed sessions this week."}
            </Typography>
          </Paper>
        ) : (
          <Stack gap={2}>
            {shownNewBlocks.length > 0 && (
              <Stack gap={2} data-testid="new-section">
                <Stack direction="row" alignItems="center" gap={1}>
                  <Chip size="small" color="info" label="New" />
                  <Typography variant="h5" fontWeight="bold">
                    Marked unconfirmed in the last {prefs.coversNewHours} hours
                  </Typography>
                </Stack>
                {shownNewBlocks.map((block) => renderBlock(block, true))}
                {shownBlocks.length > 0 && (
                  <Typography variant="h5" fontWeight="bold" mt={1}>
                    Earlier
                  </Typography>
                )}
              </Stack>
            )}
            {shownBlocks.map((block) => renderBlock(block, false))}
          </Stack>
        )}

        {filter === "covered" && coveredBlocks.length === 0 && (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">No covered sessions this week.</Typography>
          </Paper>
        )}

        {filter !== "pending" && filter !== "notsent" && coveredBlocks.length > 0 && (
          <Stack gap={2} mt={filter === "covered" ? 0 : 3}>
            {filter === "all" && (
              <Typography variant="h5" fontWeight="bold" color="success.main">
                Covered this week
              </Typography>
            )}
            {coveredBlocks.map((block) => {
              const coverNames = [...new Set(block.lessons.map((l) => l.coverTutorName).filter(Boolean))];
              return (
                <Paper
                  key={`covered-${block.id}`}
                  data-testid="covered-block"
                  sx={{
                    overflow: "hidden",
                    bgcolor: isDark ? "rgba(102,187,106,0.08)" : "#E8F5E9",
                    border: `1px solid ${isDark ? "#2E7D32" : "#66BB6A"}`,
                    color: isDark ? "inherit" : "#0F3D14",
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      borderLeft: "6px solid #43A047",
                    }}
                  >
                    <Box flex={1}>
                      <Typography variant="h5" fontWeight="bold">
                        {block.tutorName}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {fmtDay(block.lessons[0].startDateTime)} · {fmtRange(block.startMs, block.endMs)} ·{" "}
                        {block.hours} hr{block.hours === 1 ? "" : "s"}
                        {block.locationNames.length > 0 && ` · ${block.locationNames.join(", ")}`}
                      </Typography>
                    </Box>
                    <Chip size="small" color="success" label={`Covered by ${coverNames.join(", ") || "a tutor"}`} />
                  </Box>

                  {renderStrips(block)}

                  <Box>
                    {block.lessons.map((l) => (
                      <Box
                        key={l.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2,
                          py: 1,
                          borderTop: `1px solid ${isDark ? "#2E7D3255" : "#C8E6C9"}`,
                        }}
                      >
                        <Typography sx={{ minWidth: 150 }} fontWeight="bold">
                          {fmtRange(l.startDateTime, l.endDateTime)}
                        </Typography>
                        <Box flex={1}>
                          <Typography>{l.subjectGroupName || "Lesson"}</Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {(l.studentNames || []).join(", ") || "No students"}
                            {l.locationName && ` · ${l.locationName}`}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {l.coverTutorName}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* Sticky action bar */}
      {selected.size > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            px: 3,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            zIndex: 10,
            bgcolor: colors.primary[400],
          }}
        >
          <Typography>
            <b>{selected.size}</b> session{selected.size === 1 ? "" : "s"} selected
          </Typography>
          {selectedTutorCount > 1 && (
            <Typography variant="body2" color="text.secondary">
              across {selectedTutorCount} tutors
            </Typography>
          )}
          <Button size="small" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={<GroupAddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create cover request
          </Button>
        </Paper>
      )}

      <CreateCoverGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        sessions={selectedLessons}
        tutors={tutors}
        weekLessons={lessons}
        templates={templates}
        isManager={isManager}
        onCreated={() => {
          setCreateOpen(false);
          setSelected(new Set());
          toast.success("Cover request sent");
        }}
      />

      <CoverTemplatesDialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        templates={templates}
        hasSaved={hasSavedTemplates}
      />

      <CoverGroupDetailDialog
        groupId={detailGroupId}
        open={!!detailGroupId}
        onClose={() => setDetailGroupId(null)}
        tutors={tutors}
        weekLessons={lessons}
        isManager={isManager}
        currentUserUid={currentUser?.uid}
      />
    </Box>
  );
};

export default CoversPage;
